<template>
  <div class="closest-courses">
    <div class="flex p-16">
      <img
        :src="$publicUrl('images/kartu-prakerja/img_small_calendar.webp')"
        alt="closest_courses"
        class="mr-8"
        loading="lazy"
        width="32"
        height="32"
      />
      <div>
        <BlTextAv weight="bold" variation="body-16" tag="h2">Kelas dengan jadwal terdekat</BlTextAv>
        <BlTextAv weight="bold" variation="body-14" tag="p" data-testid="closest-courses-date" class="text-[#2B4AC7]" is-bolder>
          {{ closestDate }}
        </BlTextAv>
      </div>
    </div>

    <div class="ph-16 flex justify-between mb-16">
      <BlButtonAv
        class="c-button mr-8"
        variation="naked"
        size="extra-small"
        data-testid="button-course-type"
        fullwidth
        style="flex: 1"
        @click="courseTypePickerActive = true"
      >
        {{ selectedCourseTypeName }}
        <IcoChevronDownMinor size="small" color="#d6d6d6" />
      </BlButtonAv>
      <BlButtonAv
        class="c-button"
        variation="naked"
        size="extra-small"
        data-testid="button-time-frame"
        fullwidth
        style="flex: 1"
        @click="timeFramePickerActive = true"
      >
        {{ selectedTimeFrameName }}
        <IcoChevronDownMinor size="small" color="#d6d6d6" />
      </BlButtonAv>
    </div>

    <div v-if="isLoadingClosestCourses" data-testid="closest_course_shimmer" class="space-y-2 pb-28">
      <div class="courses-group ph-16 flex scroll-pl-4 space-x-2 overflow-x-auto snap-x">
        <div v-for="i in 3" :key="i" class="snap-start w-[148px] shrink-0 first:pl-6">
          <PrakerjaCardSmallShimmer data-testid="closest_course_shimmer" />
        </div>
      </div>
    </div>

    <div v-else-if="isEmptyClass" data-testid="closest_course_empty" class="h-60 flex items-center flex-col justify-center">
      <img
        :src="$publicUrl('images/kartu-prakerja/img_small_zoom_search.webp')"
        alt="closest_courses_empty"
        class="mb-8"
        loading="lazy"
        width="64"
        height="64"
      />
      <BlTextAv variation="subheading-18" class="mb-8" tag="p" weight="bold">Belum ada jadwal kelas</BlTextAv>
      <BlTextAv variation="body-14" tag="p">Coba cek tipe kelas atau jam lainnya, ya.</BlTextAv>
    </div>

    <div v-else data-testid="closest_course_list" class="space-y-2 pb-28">
      <div class="courses-group ph-16 flex scroll-pl-4 space-x-2 overflow-x-auto snap-x">
        <div v-for="course in displayFirstTenCourses" :key="course.id" class="snap-start w-[148px] shrink-0 first:pl-6">
          <PrakerjaCard
            :course="serializeItemToCourse(course)"
            :course-type="serializeItemToCourse(course).courseType"
            class="min-w-[148px]"
            referrer="prakerja_kelas_terdekat"
            is-hide-border
          />
        </div>
        <LihatSemuaCard v-if="isShowLihatSemua" :redirect-link="redirectLink()" is-hide-border />
      </div>
    </div>

    <BlPickerListMv
      class="sheet"
      title="Tipe kelas"
      data-testid="picker-course-type"
      max-height="auto"
      :is-active.sync="courseTypePickerActive"
      :picker-list="courseType"
      :item-selected="selectedCourseTypeName"
      @selectedItem="handleChangeCourseType"
    />
    <BlPickerListMv
      class="sheet"
      title="Waktu"
      max-height="auto"
      data-testid="picker-time-frame"
      :is-active.sync="timeFramePickerActive"
      :picker-list="timeFrame"
      :item-selected="selectedTimeFrameName"
      @selectedItem="handleChangeTimeFrame"
    />
  </div>
</template>

<script>
import PrakerjaCard from '../PrakerjaCard/PrakerjaCard.vue';
import LihatSemuaCard from '../LihatSemuaCard/LihatSemuaCard.vue';
import {mapGetters} from 'vuex';
import {BlTextAv, BlButtonAv, BlPickerListMv} from '@bukalapak/bazaar-mweb-latest';
import {IcoChevronDownMinor} from '@bukalapak/bazaar-visual-next';
import prakerjaClosestCourses from 'src/shared/mixins/prakerja-closest-course';
import PrakerjaCardSmallShimmer from '../PrakerjaCardSmallShimmer/PrakerjaCardSmallShimmer';
import trackerGenerator from '../../mixins/tracker-generator';
import {TRACKER_EVENT_VP_PRAKERJA_KELAS_TERDEKAT_FILTER_CLICK} from '../../config/tracker-events';

export default {
  name: 'PrakerjaBestCourses',
  components: {
    BlTextAv,
    BlButtonAv,
    BlPickerListMv,
    IcoChevronDownMinor,
    PrakerjaCard,
    LihatSemuaCard,
    PrakerjaCardSmallShimmer,
  },
  mixins: [prakerjaClosestCourses, trackerGenerator],
  data() {
    return {
      courseTypePickerActive: false,
      timeFramePickerActive: false,
    };
  },
  computed: {
    ...mapGetters('courses', ['getBestCourses']),
    selectedCourseTypeName() {
      return this.courseType.find(({value}) => value === this.courseTypeValue.value)?.name;
    },
    selectedTimeFrameName() {
      return this.timeFrame.find(({value}) => value === this.timeFrameValue.value)?.name;
    },
  },
  methods: {
    handleChangeCourseType(value) {
      this.courseTypeValue = value;
      this.sendTracker();
    },
    handleChangeTimeFrame(value) {
      this.timeFrameValue = value;
      this.sendTracker();
    },
    sendTracker() {
      const trackerPayload = {
        course_type: this.courseTypeValue.value,
        course_timeframe: this.timeFrameValue.value,
        source: this.$route.query.source || 'direct-access',
        referrer: this.$route.query.referrer || '',
      };
      this.mixinSendTracker(TRACKER_EVENT_VP_PRAKERJA_KELAS_TERDEKAT_FILTER_CLICK, trackerPayload);
    },
  },
};
</script>

<style lang="scss" scoped>
.closest-courses {
  background-color: #e4e4f7;
  .card-wrapper {
    padding: 10px;
  }
}
</style>

<style lang="scss">
.closest-courses {
  .c-button {
    border-color: #d6d6d6;
    border-radius: 40px;
    .bl-text {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #141414;
      font-weight: 400;
    }
  }
  .sheet {
    .bl-sheet__nav-bar {
      height: auto;
    }
  }
}
</style>
